import { marked } from 'marked';
import { memo, useMemo, useState, type ReactNode } from 'react';
import Markdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { JsonViewer } from '@/components/ui/json-viewer';
import { REHYPE_PLUGINS } from '@/components/ui/smoothdown/plugins';
import { cn } from '@/lib/utils';

import type {
  AssistantMessage,
  ChatMessage,
  ToolMessage,
  UserMessage,
} from '../chat-debug.types';

const REMARK_PLUGINS = [remarkGfm];

export function ChainOfThoughtsViewer({
  responses,
}: {
  responses: ChatMessage[];
}) {
  if (responses.length === 0) {
    return (
      <YStack className="py-4 items-center justify-center">
        <span className="text-sm text-foreground-subtle">
          No responses available
        </span>
      </YStack>
    );
  }

  return (
    <YStack className="py-3 gap-4">
      {responses.map((message, index) => (
        <MessageCard key={message.id} message={message} index={index + 1} />
      ))}
    </YStack>
  );
}

function MessageCard({
  message,
  index,
}: {
  message: ChatMessage;
  index: number;
}) {
  switch (message.role) {
    case 'user':
      return <UserMessageCard message={message} index={index} />;
    case 'assistant':
      return <AssistantMessageCard message={message} index={index} />;
    case 'tool':
      return <ToolMessageCard message={message} index={index} />;
  }
}

function UserMessageCard({
  message,
  index,
}: {
  message: UserMessage;
  index: number;
}) {
  const formattedDate = useMemo(() => {
    if (!message.created_at) return null;
    const date = new Date(message.created_at);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [message.created_at]);

  return (
    <YStack className="gap-3 py-3 border-t border-border first:border-t-0">
      <XStack className="items-center justify-between gap-2 flex-wrap">
        <XStack className="items-center gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            #{index}
          </span>
          <RoleBadge role="user" />
          <span className="text-xs font-medium text-foreground-muted">
            {message.agent ?? 'User'}
          </span>
        </XStack>
        <XStack className="items-center gap-2">
          {formattedDate && (
            <span className="text-xs text-foreground-subtle">
              {formattedDate}
            </span>
          )}
        </XStack>
      </XStack>

      {message.texts && message.texts.length > 0 && (
        <CollapsibleSection title="Text" defaultOpen>
          <YStack className="gap-2">
            {message.texts.map((text) => (
              <CopyableBlock key={message.id} text={text}>
                <div className="text-sm text-foreground-muted whitespace-pre-wrap font-mono bg-surface/50 p-4 rounded border border-border overflow-x-auto select-text">
                  {text}
                </div>
              </CopyableBlock>
            ))}
          </YStack>
        </CollapsibleSection>
      )}

      {message.uploads && message.uploads.length > 0 && (
        <CollapsibleSection title={`Uploads (${message.uploads.length})`}>
          <JsonViewer
            data={message.uploads}
            id={`${message.id ?? index}-uploads`}
          />
        </CollapsibleSection>
      )}

      {message.file_ids && message.file_ids.length > 0 && (
        <CollapsibleSection title={`File IDs (${message.file_ids.length})`}>
          <YStack className="gap-1">
            {message.file_ids.map((fileId, idx) => (
              <span
                key={`${message.id ?? index}-file-${idx}`}
                className="text-xs font-mono text-foreground-muted"
              >
                {fileId}
              </span>
            ))}
          </YStack>
        </CollapsibleSection>
      )}
    </YStack>
  );
}

function AssistantMessageCard({
  message,
  index,
}: {
  message: AssistantMessage;
  index: number;
}) {
  const formattedDate = useMemo(() => {
    if (!message.created_at) return null;
    const date = new Date(message.created_at);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [message.created_at]);

  return (
    <YStack className="gap-3 py-3 border-t border-border first:border-t-0">
      <XStack className="items-center justify-between gap-2 flex-wrap">
        <XStack className="items-center gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            #{index}
          </span>
          <RoleBadge role="assistant" />
          <span className="text-xs font-medium text-foreground-muted">
            {message.agent ?? 'Assistant'}
          </span>
        </XStack>
        <XStack className="items-center gap-2">
          {formattedDate && (
            <span className="text-xs text-foreground-subtle">
              {formattedDate}
            </span>
          )}
        </XStack>
      </XStack>

      {message.thought?.trim() && (
        <CollapsibleSection title="Thought" defaultOpen>
          <ThoughtMarkdown content={message.thought} id={message.id} />
        </CollapsibleSection>
      )}

      {message.texts && message.texts.length > 0 && (
        <CollapsibleSection title="Text" defaultOpen>
          <YStack className="gap-2">
            {message.texts.map((text) => (
              <CopyableBlock key={message.id} text={text}>
                <div className="text-sm text-foreground-muted whitespace-pre-wrap font-mono bg-surface/50 p-4 rounded border border-border overflow-x-auto select-text">
                  {text}
                </div>
              </CopyableBlock>
            ))}
          </YStack>
        </CollapsibleSection>
      )}

      {message.system_prompt && (
        <CollapsibleSection title="System Prompt">
          <CopyableBlock text={message.system_prompt}>
            <div className="text-xs text-foreground-muted whitespace-pre-wrap font-mono bg-surface p-3 rounded border border-border max-h-48 overflow-y-auto select-text">
              {message.system_prompt}
            </div>
          </CopyableBlock>
        </CollapsibleSection>
      )}

      {message.tool_use && message.tool_use.length > 0 && (
        <CollapsibleSection title={`Tool Calls (${message.tool_use.length})`}>
          <YStack className="gap-2">
            {message.tool_use.map((toolCall) => (
              <YStack
                key={message.id}
                className="bg-surface/50 p-3 rounded border border-border"
              >
                <XStack className="items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-foreground">
                    {toolCall.func_name}
                  </span>
                  <span className="text-xs text-foreground-subtle">
                    #{toolCall.index}
                  </span>
                </XStack>
                <pre className="text-xs text-foreground-muted font-mono whitespace-pre-wrap overflow-x-auto">
                  {formatToolArgs(toolCall.func_args)}
                </pre>
              </YStack>
            ))}
          </YStack>
        </CollapsibleSection>
      )}

      {message.invocations && message.invocations.length > 0 && (
        <CollapsibleSection
          title={`Invocations (${message.invocations.length})`}
        >
          <JsonViewer
            data={message.invocations}
            id={`${message.id ?? index}-invocations`}
          />
        </CollapsibleSection>
      )}

      {message.context && message.context.length > 0 && (
        <CollapsibleSection title={`Context (${message.context.length})`}>
          <JsonViewer data={message.context} id={message.id} />
        </CollapsibleSection>
      )}

      {message.sources && (
        <CollapsibleSection title="Sources">
          <div className="text-xs text-foreground-muted whitespace-pre-wrap font-mono bg-surface p-3 rounded border border-border">
            {message.sources}
          </div>
        </CollapsibleSection>
      )}
    </YStack>
  );
}
function ToolMessageCard({
  message,
  index,
}: {
  message: ToolMessage;
  index: number;
}) {
  const formattedDate = useMemo(() => {
    if (!message.created_at) return null;
    const date = new Date(message.created_at);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [message.created_at]);

  return (
    <YStack className="gap-3 py-3 border-t border-border first:border-t-0">
      <XStack className="items-center justify-between gap-2 flex-wrap">
        <XStack className="items-center gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            #{index}
          </span>
          <RoleBadge role="tool" />
          <span className="text-xs font-medium text-foreground-muted">
            Tool Results
          </span>
        </XStack>
        <XStack className="items-center gap-2">
          {formattedDate && (
            <span className="text-xs text-foreground-subtle">
              {formattedDate}
            </span>
          )}
        </XStack>
      </XStack>

      {message.tool_results && message.tool_results.length > 0 && (
        <CollapsibleSection
          title={`Results (${message.tool_results.length})`}
          defaultOpen
        >
          <YStack className="gap-2">
            {message.tool_results.map((result, idx) => (
              <YStack
                key={`${message.id ?? index}-result-${idx}`}
                className="bg-surface/50 p-3 rounded border border-border"
              >
                <XStack className="items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-foreground">
                    {result.func_name}
                  </span>
                </XStack>
                <pre className="text-xs text-foreground-muted font-mono whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">
                  {result.result}
                </pre>
              </YStack>
            ))}
          </YStack>
        </CollapsibleSection>
      )}
    </YStack>
  );
}

function RoleBadge({ role }: { role: 'assistant' | 'user' | 'tool' }) {
  return (
    <span
      className={cn(
        'text-xs px-1.5 py-0.5 rounded font-medium uppercase',
        role === 'assistant' && 'bg-blue-500/20 text-blue-400',
        role === 'user' && 'bg-green-500/20 text-green-400',
        role === 'tool' && 'bg-amber-500/20 text-amber-400'
      )}
    >
      {role}
    </span>
  );
}

function CopyableBlock({
  text,
  children,
}: {
  text: string;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative group/copy">
      {children}
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 opacity-0 group-hover/copy:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]/copy:opacity-100 transition-opacity p-1 rounded bg-surface hover:bg-secondary-hover text-foreground-subtle hover:text-foreground"
        title="Copy to clipboard"
      >
        {copied ? (
          <Icons.check className="size-3 text-green-400" />
        ) : (
          <Icons.copy className="size-3" />
        )}
      </button>
    </div>
  );
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <XStack className="items-center gap-1.5 text-xs font-medium text-foreground-muted hover:text-foreground transition-colors">
          <Icons.chevronDown
            className={cn(
              'size-3 transition-transform duration-200 -rotate-90',
              isOpen && 'rotate-0'
            )}
          />
          <span className="uppercase">{title}</span>
        </XStack>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 pl-3 ml-1 border-l-2 border-border-strong">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function ThoughtMarkdown({ content, id }: { content: string; id: string }) {
  const blocks = useMemo(
    () => marked.lexer(content).filter((block) => block.type !== 'space'),
    [content]
  );

  return (
    <div className="[&>*:first-child_p]:mt-0">
      {blocks.map((block, index) => (
        <ThoughtMarkdownBlock
          content={block.raw}
          key={`${id}-thought-block_${index}`}
        />
      ))}
    </div>
  );
}

// Props type for markdown component overrides
type ChildrenProps = ExtraProps & {
  children?: ReactNode;
};

const COMPONENTS: Partial<Components> = {
  p: ({ children }: ChildrenProps) => (
    <p className="text-sm text-foreground-muted mt-2 first:mt-0">{children}</p>
  ),
  strong: ({ children }: ChildrenProps) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  h1: ({ children }: ChildrenProps) => (
    <h1 className="text-base font-semibold text-foreground mt-3 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: ChildrenProps) => (
    <h2 className="text-sm font-semibold text-foreground mt-3 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }: ChildrenProps) => (
    <h3 className="text-sm font-semibold text-foreground mt-2 first:mt-0">
      {children}
    </h3>
  ),
  ul: ({ children }: ChildrenProps) => (
    <ul className="text-sm text-foreground-muted mt-2 ml-4 list-disc">
      {children}
    </ul>
  ),
  ol: ({ children }: ChildrenProps) => (
    <ol className="text-sm text-foreground-muted mt-2 ml-4 list-decimal">
      {children}
    </ol>
  ),
  li: ({ children }: ChildrenProps) => (
    <li className="text-sm mt-1">{children}</li>
  ),
  code: ({ children }: ChildrenProps) => (
    <code className="text-xs font-mono text-foreground-muted bg-surface px-1 py-0.5 rounded">
      {children}
    </code>
  ),
};

const ThoughtMarkdownBlock = memo(
  ({ content }: { content: string }) => (
    <div className={cn('transform-gpu')}>
      <Markdown
        rehypePlugins={REHYPE_PLUGINS}
        remarkPlugins={REMARK_PLUGINS}
        components={COMPONENTS}
      >
        {content}
      </Markdown>
    </div>
  ),
  (prevProps, nextProps) => prevProps.content === nextProps.content
);

ThoughtMarkdownBlock.displayName = 'ThoughtMarkdownBlock';

function formatToolArgs(args: string): string {
  try {
    const parsed = JSON.parse(args);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return args;
  }
}
